import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Clerk } from '@clerk/clerk-js';
import { UserResource } from '@clerk/types';
import { posthog } from 'posthog-js';
import { from, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserClerkPublicMetadata } from './user-clerk';

@Injectable({
  providedIn: 'root',
})
export class UserClerkService {
  private _clerk?: Clerk;
  private _user?: any;

  user = signal<UserResource | undefined>(undefined);

  constructor(private router: Router) {}

  init$() {
    if (!this._clerk) {
      const clerkPublishableKey = environment.clerkKey;
      this._clerk = new Clerk(clerkPublishableKey);
      return from(
        this._clerk.load({
          // Set load options here...
        })
      ).pipe(
        map(() => {
          return this._clerk;
        })
      );
    }
    return of(this._clerk);
  }

  reloadUser$() {
    return from(this._clerk!.user!.reload());
  }

  async getToken() {
    return await this._clerk?.session?.getToken({ template: 'ChromeExtension' });
  }

  getUser() {
    if (!this._clerk?.user) {
      this.router.navigate(['/login']);
      return undefined;
    }

    if (this._user) {
      return this._user as UserResource;
    }

    return this._clerk?.user;
  }

  getEmail() {
    return this.getUser()?.primaryEmailAddress?.emailAddress;
  }

  portraitUrl = signal(this._clerk?.user?.imageUrl ?? '');

  get publicMetadata() {
    return this.getUser()?.publicMetadata as UserClerkPublicMetadata;
  }

  credits = signal<number>(0);

  signIn() {
    this._clerk?.openSignIn({
      signUpUrl: '/signup',
    });
  }

  mountSignup(node: HTMLDivElement, options?: { fallbackRedirectUrl?: string }) {
    this._clerk?.mountSignUp(node, { signInUrl: '/login', fallbackRedirectUrl: options?.fallbackRedirectUrl });
  }

  mountSignIn(node: HTMLDivElement, options?: { fallbackRedirectUrl?: string }) {
    this._clerk?.mountSignIn(node, { signUpUrl: '/signup', fallbackRedirectUrl: options?.fallbackRedirectUrl });
  }

  mountUserProfile(node: HTMLDivElement) {
    this._clerk?.mountUserProfile(node);
  }

  getAccessToken$() {
    const session = this._clerk?.session;

    if (!session) {
      this.router.navigate(['/login']);
      return of('');
    }

    return from(session.getToken());
  }

  signOut$(redirectUrl?: string) {
    posthog.reset();
    return from(this._clerk!.signOut({ redirectUrl }));
  }
}
